import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { SessionStatus } from 'ngx-core-services';

import { SessionSummary, UpgradeLogFilter } from '../upgrade-logs-viewer.model';

@Injectable({
  providedIn: 'root'
})
export class UpgradeLogsFilterService {

  // Filter logic
  applyFilters(sessions: SessionSummary[], filters: UpgradeLogFilter): SessionSummary[] {
    let filteredSessions = [...sessions];

    // Filter by device serial
    if (filters.deviceSerial?.trim()) {
      const serial = filters.deviceSerial.toLowerCase();
      filteredSessions = filteredSessions.filter(session =>
        session.deviceSerial.toLowerCase().includes(serial)
      );
    }

    // Filter by date range
    if (filters.dateRange) {
      filteredSessions = this.filterByDateRange(
        filteredSessions,
        filters.dateRange.from,
        filters.dateRange.to
      );
    }

    // Filter by operator
    if (filters.operatorId?.trim()) {
      const operatorId = filters.operatorId.toLowerCase();
      filteredSessions = filteredSessions.filter(session =>
        session.operatorName.toLowerCase().includes(operatorId)
      );
    }

    // Filter by status
    if (filters.status?.length) {
      filteredSessions = this.filterByStatus(filteredSessions, filters.status);
    }

    // Filter by modules
    if (filters.modules?.length) {
      filteredSessions = filteredSessions.filter(session =>
        filters.modules!.some(module =>
          session.modules.includes(module)
        )
      );
    }

    return filteredSessions;
  }

  // Search utilities
  searchByDeviceSerial(serial: string): Observable<SessionSummary[]> {
    // This would integrate with UpgradeLogsViewerService
    // For now, return empty observable
    return new Observable(observer => observer.next([]));
  }

  filterByStatus(sessions: SessionSummary[], status: SessionStatus[]): SessionSummary[] {
    return sessions.filter(session => status.includes(session.status));
  }

  filterByDateRange(sessions: SessionSummary[], from: Date, to: Date): SessionSummary[] {
    const fromTime = from.getTime();
    const toTime = to.getTime();

    return sessions.filter(session => {
      const sessionTime = session.startTime;
      return sessionTime >= fromTime && sessionTime <= toTime;
    });
  }

  // Validation
  validateDeviceSerial(serial: string): boolean {
    if (!serial?.trim()) return false;

    // Basic validation - alphanumeric, dashes, underscores
    const pattern = /^[a-zA-Z0-9_-]+$/;
    return pattern.test(serial.trim()) && serial.trim().length >= 3;
  }

  validateDateRange(from: Date, to: Date): boolean {
    if (!from || !to) return false;

    const now = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(now.getFullYear() - 1);

    // Date range should be valid and not too far in the past/future
    return from <= to &&
           from >= oneYearAgo &&
           to <= now;
  }

  formatDuration(milliseconds?: number): string {
    if (!milliseconds) return 'N/A';

    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  }

  formatTimestamp(timestamp: number): string {
    return new Date(timestamp).toLocaleString();
  }
}
